import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Cpu,
  Brain,
  Puzzle,
  Sparkles,
  User as UserIcon,
  Bot,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
} from "lucide-react";
import api from "../services/api";
import { formatDateTimeWithSeconds, formatTimeWithSeconds } from "../utils/dateFormatter";
import {
  formatDuration,
} from "../utils/performanceFormatter";

/**
 * Session Detail Page - Conversation View
 *
 * Displays chat conversation with:
 * - User message on the left
 * - Bot response on the right
 * - Processing summary in the middle
 * - Expandable rows to show full pipeline details
 */

interface SessionMessage {
  id: string;
  direction: string;
  rawMessage: any;
  llmInterpretation: any;
  llmPrompt: string | null;
  pluginUsed: string | null;
  pluginOutput: any;
  llmFinalResponse: any;
  createdAt: string;
}

interface SessionDetail {
  id: string;
  createdAt: string;
  updatedAt: string;
  location: string | null;
  city: string | null;
  tableNumber: string | null;
  userAgent: string | null;
  ipAddress: string | null;
  messages: SessionMessage[];
}

interface ConversationTurn {
  userMessage?: SessionMessage;
  preprocessing?: SessionMessage;
  interpretation?: SessionMessage;
  plugin?: SessionMessage;
  llmFinal?: SessionMessage;
  response?: SessionMessage;
  timestamp: string;
}

const SessionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ sessionId?: string; "*"?: string }>();
  const sessionId = params.sessionId || params["*"];
  const isAdminContext = location.pathname.startsWith("/admin");
  const backPath = isAdminContext ? "/admin/sessions" : "/sessions";

  const [session, setSession] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTurns, setExpandedTurns] = useState<Set<number>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchSessionDetails = async () => {
    if (!sessionId) {
      setError("No session ID provided");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/sessions/${sessionId}`);

      if (response.data.success) {
        setSession(response.data.session);
      } else {
        setError(response.data.error || "Failed to load session details");
      }
    } catch (err: any) {
      console.error("Error fetching session details:", err);
      setError(err.response?.data?.error || "An error occurred while loading session details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessionDetails();
  }, [sessionId]);

  // Date formatting now handled by utils/dateFormatter with IST timezone

  // Group messages into conversation turns
  const groupIntoTurns = (messages: SessionMessage[]): ConversationTurn[] => {
    const turns: ConversationTurn[] = [];
    let currentTurn: ConversationTurn | null = null;

    messages.forEach((message) => {
      if (message.direction === "user") {
        // Start a new turn
        if (currentTurn) {
          turns.push(currentTurn);
        }
        currentTurn = {
          userMessage: message,
          timestamp: message.createdAt,
        };
      } else if (currentTurn) {
        // Add to current turn
        switch (message.direction) {
          case "preprocessing":
            currentTurn.preprocessing = message;
            break;
          case "interpretation":
            currentTurn.interpretation = message;
            break;
          case "plugin":
            currentTurn.plugin = message;
            break;
          case "llm_final":
            currentTurn.llmFinal = message;
            break;
          case "response":
            currentTurn.response = message;
            break;
        }
      }
    });

    // Add the last turn
    if (currentTurn) {
      turns.push(currentTurn);
    }

    return turns;
  };

  const toggleTurn = (index: number) => {
    const newExpanded = new Set(expandedTurns);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedTurns(newExpanded);
  };

  const extractUserMessage = (msg: SessionMessage | undefined): string => {
    if (!msg) return "";
    if (typeof msg.rawMessage === "string") return msg.rawMessage;
    if (msg.rawMessage?.message) return msg.rawMessage.message;
    return JSON.stringify(msg.rawMessage);
  };

  const extractBotResponse = (msg: SessionMessage | undefined): string => {
    if (!msg) return "";
    if (typeof msg.rawMessage === "string") return msg.rawMessage;
    if (msg.rawMessage?.response) return msg.rawMessage.response;
    if (msg.rawMessage?.text) return msg.rawMessage.text;
    if (msg.rawMessage?.content) return msg.rawMessage.content;
    return JSON.stringify(msg.rawMessage);
  };

  /**
   * Determine turn type (button_click or user_input) from available data
   * First checks rawMessage for stored interaction_type, otherwise infers from interpretation flags
   */
  const getTurnType = (turn: ConversationTurn): "button_click" | "user_input" => {
    // First check if interaction_type is stored in rawMessage
    if (turn.userMessage?.rawMessage?.interaction_type) {
      const interactionType = turn.userMessage.rawMessage.interaction_type;
      if (interactionType === "button_click" || interactionType === "user_input") {
        return interactionType;
      }
    }
    
    // Fallback: Check interpretation flags - these indicate button clicks
    if (
      turn.interpretation?.llmInterpretation?.directButtonClick ||
      turn.interpretation?.llmInterpretation?.botNavigationFastPath ||
      turn.interpretation?.llmInterpretation?.moreDetailsClick
    ) {
      return "button_click";
    }
    
    // Check if rawMessage has action field (indicates button click)
    if (turn.userMessage?.rawMessage?.action) {
      return "button_click";
    }
    
    // Default to user_input
    return "user_input";
  };

  /**
   * Extract performance data from response message
   * Performance data is stored in response.rawMessage.metadata.performance
   */
  const getPerformanceData = (turn: ConversationTurn) => {
    if (!turn.response?.rawMessage?.metadata?.performance) {
      return null;
    }
    return turn.response.rawMessage.metadata.performance;
  };

  /**
   * Get time taken for a specific process type by matching timer step names
   * Maps process types to their corresponding timer step names (kept for backward compatibility)
   * 
   * Note: For "More Details" flows, more_details_detection wraps the entire flow including
   * final_responder_more_details and build_artifacts_more_details. To avoid double-counting,
   * we exclude more_details_detection from interpretation when llm_final steps exist.
   */
  const getProcessTime = (
    performanceData: any,
    processType: "preprocessing" | "interpretation" | "plugin" | "llm_final" | "response"
  ): number | null => {
    if (!performanceData?.breakdown || !Array.isArray(performanceData.breakdown)) {
      return null;
    }

    // Check if this is a "More Details" flow with nested timers
    const hasMoreDetailsLLMFinal = performanceData.breakdown.some(
      (b: any) => b.step === "final_responder_more_details" || b.step === "final_responder_more_details_store"
    );

    // Map process types to their corresponding timer step names
    const stepMapping: Record<string, string[]> = {
      preprocessing: ["preprocessing"],
      interpretation: [
        "planner_llm",
        "button_template_check",
        "chat_history_load_backend_button",
        // Exclude more_details_detection when it wraps llm_final steps (to avoid double-counting)
        ...(hasMoreDetailsLLMFinal ? [] : ["more_details_detection"]),
        "bot_navigation_detection",
      ],
      plugin: [
        "lookups_execution",
        "item_details_fetch",
        "store_details_fetch",
        "lookups_execution_backend_button",
        "bot_navigation_query",
      ],
      llm_final: [
        "final_responder_llm",
        "final_responder_backend_button",
        "final_responder_more_details",
        "final_responder_more_details_store",
        "final_responder_bot_navigation",
      ],
      response: [
        "build_artifacts",
        "build_artifacts_backend_button",
        "build_artifacts_more_details",
        "build_artifacts_more_details_store",
        "build_artifacts_bot_navigation",
      ],
    };

    const relevantSteps = stepMapping[processType] || [];
    let totalTime = 0;

    relevantSteps.forEach((stepName) => {
      const step = performanceData.breakdown.find((b: any) => b.step === stepName);
      if (step && step.duration) {
        totalTime += step.duration;
      }
    });

    return totalTime > 0 ? Math.round(totalTime) : null;
  };

  /**
   * Check if performance data has llm_final steps (even if turn.llmFinal doesn't exist)
   * This is needed for bot navigation final-level flows where final_responder_bot_navigation
   * is executed but doesn't create a separate llm_final message
   */
  const hasLLMFinalStep = (performanceData: any): boolean => {
    if (!performanceData?.breakdown || !Array.isArray(performanceData.breakdown)) {
      return false;
    }
    const llmFinalSteps = [
      "final_responder_llm",
      "final_responder_backend_button",
      "final_responder_more_details",
      "final_responder_more_details_store",
      "final_responder_bot_navigation",
    ];
    return llmFinalSteps.some((stepName) =>
      performanceData.breakdown.some((b: any) => b.step === stepName)
    );
  };

  /**
   * Check if performance data has plugin/data-fetch steps (even if turn.plugin doesn't exist)
   * This is needed for "More Details" flows where item_details_fetch/store_details_fetch
   * are executed but don't create a separate plugin message
   */
  const hasPluginStep = (performanceData: any): boolean => {
    if (!performanceData?.breakdown || !Array.isArray(performanceData.breakdown)) {
      return false;
    }
    const pluginSteps = [
      "lookups_execution",
      "item_details_fetch",
      "store_details_fetch",
      "lookups_execution_backend_button",
      "bot_navigation_query",
    ];
    return pluginSteps.some((stepName) =>
      performanceData.breakdown.some((b: any) => b.step === stepName)
    );
  };

  /**
   * Get plugin step display info from performance breakdown
   * Returns the highest priority plugin step found with its display name
   */
  const getPluginStepInfo = (performanceData: any): { name: string; duration: number; stepName: string } | null => {
    if (!performanceData?.breakdown || !Array.isArray(performanceData.breakdown)) {
      return null;
    }
    
    // Priority order for plugin steps (most specific first)
    const stepPriority: Array<{ step: string; displayName: string }> = [
      { step: "item_details_fetch", displayName: "Item Details Fetch" },
      { step: "store_details_fetch", displayName: "Store Details Fetch" },
      { step: "bot_navigation_query", displayName: "Bot Navigation Query" },
      { step: "lookups_execution", displayName: "Database Lookups" },
      { step: "lookups_execution_backend_button", displayName: "Database Lookups (Button)" },
    ];
    
    for (const { step: stepName, displayName } of stepPriority) {
      const step = performanceData.breakdown.find((b: any) => b.step === stepName);
      if (step && step.duration) {
        return {
          name: displayName,
          duration: step.duration,
          stepName,
        };
      }
    }
    
    return null;
  };

  const copyToClipboard = async (data: any, id: string) => {
    try {
      const jsonStr = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(jsonStr);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  const copyAllConversations = async () => {
    try {
      const turns = groupIntoTurns(session?.messages || []);

      // Build a comprehensive text representation of all turns
      const allConversationsData = turns.map((turn, index) => {
        const turnData: any = {
          turnNumber: index + 1,
          timestamp: turn.timestamp,
          userMessage: null,
          preprocessing: null,
          llmInterpretation: null,
          pluginProcessing: null,
          llmFinalResponse: null,
          botResponse: null,
        };

        // User message
        if (turn.userMessage) {
          turnData.userMessage = {
            timestamp: turn.userMessage.createdAt,
            content: turn.userMessage.rawMessage,
          };
        }

        // Preprocessing
        if (turn.preprocessing) {
          turnData.preprocessing = {
            timestamp: turn.preprocessing.createdAt,
            content: turn.preprocessing.rawMessage,
          };
        }

        // LLM Interpretation
        if (turn.interpretation) {
          turnData.llmInterpretation = {
            timestamp: turn.interpretation.createdAt,
            content: turn.interpretation.llmInterpretation || turn.interpretation.rawMessage,
          };
        }

        // Plugin
        if (turn.plugin) {
          turnData.pluginProcessing = {
            timestamp: turn.plugin.createdAt,
            pluginName: turn.plugin.pluginUsed,
            content: turn.plugin.pluginOutput || turn.plugin.rawMessage,
          };
        }

        // LLM Final Response
        if (turn.llmFinal) {
          turnData.llmFinalResponse = {
            timestamp: turn.llmFinal.createdAt,
            content: turn.llmFinal.llmFinalResponse || turn.llmFinal.rawMessage,
          };
        }

        // Response
        if (turn.response) {
          turnData.botResponse = {
            timestamp: turn.response.createdAt,
            content: turn.response.rawMessage,
          };
        }

        return turnData;
      });

      const fullData = {
        sessionId: session?.id,
        sessionCreated: session?.createdAt,
        location: session?.location,
        city: session?.city,
        tableNumber: session?.tableNumber,
        totalTurns: turns.length,
        conversations: allConversationsData,
      };

      const textContent = JSON.stringify(fullData, null, 2);
      await navigator.clipboard.writeText(textContent);
      setCopiedId("copy-all");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy all conversations:", err);
    }
  };

  const renderJSON = (data: any, id: string) => {
    if (!data) return <span className="text-xs text-gray-400">null</span>;

    const jsonStr = JSON.stringify(data, null, 2);
    const isCopied = copiedId === id;

    return (
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(data, id);
          }}
          className="absolute top-2 right-2 p-2 bg-white hover:bg-gray-100 rounded border border-gray-300 transition-colors z-10"
          title="Copy JSON"
        >
          {isCopied ? (
            <Check size={16} className="text-green-600" />
          ) : (
            <Copy size={16} className="text-gray-600" />
          )}
        </button>
        <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap break-words bg-gray-50 p-3 pr-12 rounded border border-gray-200 max-h-96 overflow-auto">
          {jsonStr}
        </pre>
      </div>
    );
  };

  const renderPrompt = (prompt: string, id: string) => {
    if (!prompt) return <span className="text-xs text-gray-400">No prompt available</span>;

    const isCopied = copiedId === id;

    return (
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(prompt, id);
          }}
          className="absolute top-2 right-2 p-2 bg-white hover:bg-gray-100 rounded border border-gray-300 transition-colors z-10"
          title="Copy Prompt"
        >
          {isCopied ? (
            <Check size={16} className="text-green-600" />
          ) : (
            <Copy size={16} className="text-gray-600" />
          )}
        </button>
        <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap break-words bg-gray-50 p-3 pr-12 rounded border border-gray-200 max-h-96 overflow-auto">
          {prompt}
        </pre>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={isAdminContext ? "" : "min-h-screen bg-gray-50 p-4"}>
        <div className="max-w-7xl mx-auto space-y-6">
          <button
            onClick={() => navigate(backPath)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft size={20} />
            Back to Sessions
          </button>
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading session details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className={isAdminContext ? "" : "min-h-screen bg-gray-50 p-4"}>
        <div className="max-w-7xl mx-auto space-y-6">
          <button
            onClick={() => navigate(backPath)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft size={20} />
            Back to Sessions
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <p className="text-red-800 text-lg font-semibold">Error Loading Session</p>
            <p className="text-red-600 text-sm mt-2">{error || "Session not found"}</p>
          </div>
        </div>
      </div>
    );
  }

  const turns = groupIntoTurns(session.messages);

  return (
    <div className={isAdminContext ? "" : "min-h-screen bg-gray-50 p-4"}>
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(backPath)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft size={20} />
            Back to Sessions
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={copyAllConversations}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              title="Copy all conversation details"
            >
              {copiedId === "copy-all" ? (
                <>
                  <Check size={16} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={16} />
                  Copy All
                </>
              )}
            </button>
            <button
              onClick={fetchSessionDetails}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* Session Info */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-4 gap-4 mb-3">
            <div>
              <p className="text-sm text-gray-600">Session ID</p>
              <p className="font-mono text-sm text-gray-900">{session.id.substring(0, 16)}...</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Created</p>
              <p className="text-sm text-gray-900">
                {formatDateTimeWithSeconds(session.createdAt)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Location</p>
              <p className="text-sm text-gray-900">{session.location || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">City</p>
              <p className="text-sm text-gray-900">{session.city || "-"}</p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Table Number</p>
              <p className="text-sm text-gray-900">{session.tableNumber || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Conversation Turns</p>
              <p className="text-lg font-bold text-gray-900">{turns.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Updated</p>
              <p className="text-sm text-gray-900">
                {formatDateTimeWithSeconds(session.updatedAt)}
              </p>
            </div>
            <div>
              {/* Empty for alignment */}
            </div>
          </div>
        </div>

        {/* Conversation Turns */}
        <div className="space-y-4">
          {turns.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Clock className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600">No messages in this session</p>
            </div>
          ) : (
            turns.map((turn, index) => {
              const isExpanded = expandedTurns.has(index);
              const userMsg = extractUserMessage(turn.userMessage);
              const botMsg = extractBotResponse(turn.response);
              
              // Get performance data for this turn
              const performanceData = getPerformanceData(turn);
              const hasProcessing =
                turn.preprocessing || 
                turn.interpretation || 
                turn.plugin || 
                turn.llmFinal || 
                (performanceData && (hasLLMFinalStep(performanceData) || hasPluginStep(performanceData)));

              return (
                <div key={index} className="bg-white rounded-lg shadow overflow-hidden">
                  {/* Main Row - User Message | Processing | Bot Response */}
                  <div
                    className="grid grid-cols-12 gap-4 p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleTurn(index)}
                  >
                    {/* Left: User Message */}
                    <div className="col-span-3 flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <UserIcon size={18} className="text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs font-semibold text-gray-900">User</p>
                          {(() => {
                            const turnType = getTurnType(turn);
                            return (
                              <span
                                className={`px-1.5 py-0.5 text-xs font-semibold rounded ${
                                  turnType === "button_click"
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-gray-100 text-gray-700"
                                }`}
                                title={turnType === "button_click" ? "Button Click" : "User Input"}
                              >
                                {turnType === "button_click" ? "🔘 Click" : "⌨️ Input"}
                              </span>
                            );
                          })()}
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-3">{userMsg}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTimeWithSeconds(turn.timestamp)}
                        </p>
                      </div>
                    </div>

                    {/* Middle: Processing Summary */}
                    <div className="col-span-3 flex flex-col items-center justify-center border-x border-gray-200 px-4">
                      <div className="flex items-center gap-2 mb-2">
                        {isExpanded ? (
                          <ChevronDown size={20} className="text-gray-600" />
                        ) : (
                          <ChevronRight size={20} className="text-gray-600" />
                        )}
                        <span className="text-xs font-semibold text-gray-700">
                          Processing Pipeline
                        </span>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap justify-center">
                        {turn.preprocessing && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 rounded text-xs">
                            <Cpu size={14} className="text-purple-600" />
                            <span className="text-purple-700">Preprocess</span>
                            {performanceData && getProcessTime(performanceData, "preprocessing") !== null && (
                              <span className="text-purple-600 font-semibold">
                                {formatDuration(getProcessTime(performanceData, "preprocessing"))}
                              </span>
                            )}
                          </div>
                        )}
                        {turn.interpretation &&
                          (turn.interpretation.llmPrompt ? (
                            // LLM was called (has prompt)
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded text-xs">
                              <Brain size={14} className="text-green-600" />
                              <span className="text-green-700">LLM</span>
                              {performanceData && getProcessTime(performanceData, "interpretation") !== null && (
                                <span className="text-green-600 font-semibold">
                                  {formatDuration(getProcessTime(performanceData, "interpretation"))}
                                </span>
                              )}
                            </div>
                          ) : turn.interpretation.llmInterpretation?.moreDetailsClick ? (
                            // More Details Detection (fast path)
                            <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 rounded text-xs">
                              <Puzzle size={14} className="text-yellow-600" />
                              <span className="text-yellow-700">More Details</span>
                              {performanceData && (() => {
                                // For "More Details", show the actual more_details_detection time
                                const moreDetailsDetectionStep = performanceData.breakdown.find(
                                  (b: any) => b.step === "more_details_detection"
                                );
                                if (moreDetailsDetectionStep) {
                                  return (
                                    <span className="text-yellow-600 font-semibold">
                                      {formatDuration(moreDetailsDetectionStep.duration)}
                                    </span>
                                  );
                                }
                                // Fallback to interpretation time if more_details_detection not found
                                const interpretationTime = getProcessTime(performanceData, "interpretation");
                                if (interpretationTime !== null) {
                                  return (
                                    <span className="text-yellow-600 font-semibold">
                                      {formatDuration(interpretationTime)}
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          ) : turn.interpretation.llmInterpretation?.directButtonClick ? (
                            // Direct button template (fast path)
                            <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 rounded text-xs">
                              <Puzzle size={14} className="text-purple-600" />
                              <span className="text-purple-700">DB Fetch</span>
                              {performanceData && getProcessTime(performanceData, "interpretation") !== null && (
                                <span className="text-purple-600 font-semibold">
                                  {formatDuration(getProcessTime(performanceData, "interpretation"))}
                                </span>
                              )}
                            </div>
                          ) : turn.interpretation.llmInterpretation?.botNavigationFastPath ? (
                            // Bot navigation fast path (no LLM)
                            <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 rounded text-xs">
                              <Puzzle size={14} className="text-blue-600" />
                              <span className="text-blue-700">Bot Nav</span>
                              {performanceData && getProcessTime(performanceData, "interpretation") !== null && (
                                <span className="text-blue-600 font-semibold">
                                  {formatDuration(getProcessTime(performanceData, "interpretation"))}
                                </span>
                              )}
                            </div>
                          ) : (
                            // No prompt = fast path (fallback)
                            <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs">
                              <Puzzle size={14} className="text-gray-600" />
                              <span className="text-gray-700">Fast Path</span>
                              {performanceData && getProcessTime(performanceData, "interpretation") !== null && (
                                <span className="text-gray-600 font-semibold">
                                  {formatDuration(getProcessTime(performanceData, "interpretation"))}
                                </span>
                              )}
                            </div>
                          ))}
                        {(turn.plugin || (performanceData && hasPluginStep(performanceData))) && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 rounded text-xs">
                            <Puzzle size={14} className="text-orange-600" />
                            <span className="text-orange-700">
                              {turn.plugin?.pluginUsed || 
                               (performanceData && getPluginStepInfo(performanceData)?.name) || 
                               "Plugin"}
                            </span>
                            {performanceData && (() => {
                              // If we have a plugin step info, show its duration directly
                              // Otherwise, use the aggregated plugin time
                              const pluginStepInfo = getPluginStepInfo(performanceData);
                              if (pluginStepInfo && !turn.plugin) {
                                return (
                                  <span className="text-orange-600 font-semibold">
                                    {formatDuration(pluginStepInfo.duration)}
                                  </span>
                                );
                              }
                              const pluginTime = getProcessTime(performanceData, "plugin");
                              if (pluginTime !== null) {
                                return (
                                  <span className="text-orange-600 font-semibold">
                                    {formatDuration(pluginTime)}
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                        {(turn.llmFinal || (performanceData && hasLLMFinalStep(performanceData))) && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-pink-100 rounded text-xs">
                            <Sparkles size={14} className="text-pink-600" />
                            <span className="text-pink-700">Format</span>
                            {performanceData && getProcessTime(performanceData, "llm_final") !== null && (
                              <span className="text-pink-600 font-semibold">
                                {formatDuration(getProcessTime(performanceData, "llm_final"))}
                              </span>
                            )}
                          </div>
                        )}
                        {turn.response && performanceData && getProcessTime(performanceData, "response") !== null && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded text-xs">
                            <Sparkles size={14} className="text-green-600" />
                            <span className="text-green-700">Response</span>
                            <span className="text-green-600 font-semibold">
                              {formatDuration(getProcessTime(performanceData, "response"))}
                            </span>
                          </div>
                        )}
                      </div>

                      {!hasProcessing && (
                        <span className="text-xs text-gray-400">No processing data</span>
                      )}
                      
                      {/* Total Time Display */}
                      {performanceData?.totalTime && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <div className="flex items-center gap-1 px-2 py-1 bg-indigo-100 rounded text-xs">
                            <Clock size={12} className="text-indigo-600" />
                            <span className="text-indigo-700 font-semibold">
                              Total: {formatDuration(performanceData.totalTime)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right: Bot Response */}
                    <div className="col-span-6 flex gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <Bot size={18} className="text-green-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-900 mb-1">Chatbot</p>
                        <p className="text-sm text-gray-700 line-clamp-3">
                          {botMsg || <span className="text-gray-400 italic">No response</span>}
                        </p>
                        {turn.response && (
                          <p className="text-xs text-gray-500 mt-1">
                            {formatTimeWithSeconds(turn.response.createdAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Section - Full Pipeline Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <h4 className="text-sm font-bold text-gray-900 mb-4">
                        Complete Pipeline Details
                      </h4>

                      <div className="space-y-4">
                        {/* User Message */}
                        {turn.userMessage && (
                          <div className="bg-white rounded-lg p-4 border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                              <UserIcon size={16} className="text-blue-600" />
                              <h5 className="text-sm font-semibold text-gray-900">User Message</h5>
                              {(() => {
                                const turnType = getTurnType(turn);
                                return (
                                  <span
                                    className={`px-2 py-0.5 text-xs font-semibold rounded ${
                                      turnType === "button_click"
                                        ? "bg-blue-100 text-blue-700"
                                        : "bg-gray-100 text-gray-700"
                                    }`}
                                  >
                                    {turnType === "button_click" ? "🔘 Button Click" : "⌨️ User Input"}
                                  </span>
                                );
                              })()}
                              <span className="text-xs text-gray-500 ml-auto">
                                {formatTimeWithSeconds(turn.userMessage.createdAt)}
                              </span>
                            </div>
                            {renderJSON(turn.userMessage.rawMessage, `user-${index}`)}
                          </div>
                        )}

                        {/* Preprocessing */}
                        {turn.preprocessing && (
                          <div className="bg-white rounded-lg p-4 border border-purple-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Cpu size={16} className="text-purple-600" />
                              <h5 className="text-sm font-semibold text-gray-900">Preprocessing</h5>
                              <span className="text-xs text-gray-500">
                                {formatTimeWithSeconds(turn.preprocessing.createdAt)}
                              </span>
                              {performanceData && getProcessTime(performanceData, "preprocessing") !== null && (
                                <span className="ml-auto flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                                  <Clock size={12} />
                                  {formatDuration(getProcessTime(performanceData, "preprocessing"))}
                                </span>
                              )}
                            </div>
                            {renderJSON(turn.preprocessing.rawMessage, `preprocessing-${index}`)}
                          </div>
                        )}

                        {/* LLM Interpretation / Button Template Fetch / Bot Navigation */}
                        {turn.interpretation && (
                          <div
                            className={`bg-white rounded-lg p-4 border ${
                              turn.interpretation.llmInterpretation?.moreDetailsClick 
                                ? "border-yellow-200" 
                                : turn.interpretation.llmInterpretation?.directButtonClick 
                                ? "border-purple-200" 
                                : turn.interpretation.llmInterpretation?.botNavigationFastPath
                                ? "border-blue-200"
                                : "border-green-200"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              {turn.interpretation.llmInterpretation?.moreDetailsClick ? (
                                <>
                                  <Puzzle size={16} className="text-yellow-600" />
                                  <h5 className="text-sm font-semibold text-gray-900">
                                    More Details Detection
                                  </h5>
                                </>
                              ) : turn.interpretation.llmInterpretation?.directButtonClick ? (
                                <>
                                  <Puzzle size={16} className="text-purple-600" />
                                  <h5 className="text-sm font-semibold text-gray-900">
                                    Button Template Fetch (Database)
                                  </h5>
                                </>
                              ) : turn.interpretation.llmInterpretation?.botNavigationFastPath ? (
                                <>
                                  <Puzzle size={16} className="text-blue-600" />
                                  <h5 className="text-sm font-semibold text-gray-900">
                                    Bot Navigation Fast Path (Database)
                                  </h5>
                                </>
                              ) : (
                                <>
                                  <Brain size={16} className="text-green-600" />
                                  <h5 className="text-sm font-semibold text-gray-900">
                                    LLM Interpretation (Planner)
                                  </h5>
                                </>
                              )}
                              <span className="text-xs text-gray-500">
                                {formatTimeWithSeconds(turn.interpretation.createdAt)}
                              </span>
                              {performanceData && (() => {
                                // For "More Details" flows, show the actual more_details_detection time
                                // which includes the detection overhead
                                if (turn.interpretation.llmInterpretation?.moreDetailsClick) {
                                  const moreDetailsDetectionStep = performanceData.breakdown.find(
                                    (b: any) => b.step === "more_details_detection"
                                  );
                                  if (moreDetailsDetectionStep) {
                                    return (
                                      <span className="ml-auto flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">
                                        <Clock size={12} />
                                        {formatDuration(moreDetailsDetectionStep.duration)}
                                      </span>
                                    );
                                  }
                                }
                                // For other flows, use the standard interpretation time calculation
                                const interpretationTime = getProcessTime(performanceData, "interpretation");
                                if (interpretationTime !== null) {
                                  return (
                                    <span className={`ml-auto flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                                      turn.interpretation.llmInterpretation?.directButtonClick 
                                        ? "bg-purple-100 text-purple-700"
                                        : turn.interpretation.llmInterpretation?.botNavigationFastPath
                                        ? "bg-blue-100 text-blue-700"
                                        : "bg-green-100 text-green-700"
                                    }`}>
                                      <Clock size={12} />
                                      {formatDuration(interpretationTime)}
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>

                            {/* Show Prompt if available (only for LLM, not for fast paths) */}
                            {!turn.interpretation.llmInterpretation?.moreDetailsClick &&
                              !turn.interpretation.llmInterpretation?.directButtonClick &&
                              !turn.interpretation.llmInterpretation?.botNavigationFastPath &&
                              turn.interpretation.llmPrompt && (
                                <div className="mb-4">
                                  <h6 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                      Prompt sent to LLM
                                    </span>
                                  </h6>
                                  {renderPrompt(
                                    turn.interpretation.llmPrompt,
                                    `interpretation-prompt-${index}`,
                                  )}
                                </div>
                              )}

                            {/* Show Response */}
                            <h6 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                              {turn.interpretation.llmInterpretation?.moreDetailsClick ? (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                                  More Details Information
                                </span>
                              ) : turn.interpretation.llmInterpretation?.directButtonClick ? (
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                  Template Information
                                </span>
                              ) : turn.interpretation.llmInterpretation?.botNavigationFastPath ? (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                  Bot Navigation Information
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                  LLM Response
                                </span>
                              )}
                            </h6>
                            {renderJSON(
                              turn.interpretation.llmInterpretation ||
                                turn.interpretation.rawMessage,
                              `interpretation-${index}`,
                            )}
                          </div>
                        )}

                        {/* Plugin Processing */}
                        {(turn.plugin || (performanceData && hasPluginStep(performanceData))) && (
                          <div className="bg-white rounded-lg p-4 border border-orange-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Puzzle size={16} className="text-orange-600" />
                              <h5 className="text-sm font-semibold text-gray-900">
                                {turn.plugin 
                                  ? `Plugin: ${turn.plugin.pluginUsed || "Unknown"}`
                                  : performanceData 
                                    ? `Plugin: ${getPluginStepInfo(performanceData)?.name || "Plugin Processing"}`
                                    : "Plugin Processing"}
                              </h5>
                              {turn.plugin && (
                                <span className="text-xs text-gray-500">
                                  {formatTimeWithSeconds(turn.plugin.createdAt)}
                                </span>
                              )}
                              {performanceData && (() => {
                                // If we have a plugin step info (for flows without turn.plugin), show its duration
                                // Otherwise, use the aggregated plugin time
                                const pluginStepInfo = getPluginStepInfo(performanceData);
                                if (pluginStepInfo && !turn.plugin) {
                                  return (
                                    <span className={`ml-auto flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-semibold ${!turn.plugin ? 'ml-0' : ''}`}>
                                      <Clock size={12} />
                                      {formatDuration(pluginStepInfo.duration)}
                                    </span>
                                  );
                                }
                                const pluginTime = getProcessTime(performanceData, "plugin");
                                if (pluginTime !== null) {
                                  return (
                                    <span className="ml-auto flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-semibold">
                                      <Clock size={12} />
                                      {formatDuration(pluginTime)}
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>

                            {/* Show Database Queries if available - only when turn.plugin exists */}
                            {turn.plugin && turn.plugin.pluginOutput?.metadata?.queries && (
                              <div className="mb-4">
                                <h6 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                    Database Queries
                                  </span>
                                </h6>

                                {/* Item Query */}
                                {turn.plugin.pluginOutput.metadata.queries.itemQuery && (
                                  <div className="mb-3">
                                    <p className="text-xs font-semibold text-gray-600 mb-1">
                                      Item Lookup Query:
                                    </p>
                                    
                                    {/* Smart Search Metadata */}
                                    {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata && (
                                      <div className="mb-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
                                        <div className="flex items-center gap-2 mb-2">
                                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                          <span className="text-xs font-bold text-blue-900">
                                            Smart Search Engine (3-Tier Pipeline)
                                          </span>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-3 text-xs">
                                          <div className="bg-white rounded p-2 border border-blue-100">
                                            <span className="text-gray-600">Search Query:</span>
                                            <span className="ml-1 font-mono font-semibold text-blue-900">
                                              "{turn.plugin.pluginOutput.metadata.queries.itemQuery.searchQuery}"
                                            </span>
                                          </div>
                                          
                                          <div className="bg-white rounded p-2 border border-blue-100">
                                            <span className="text-gray-600">Search Tier:</span>
                                            <span className={`ml-1 font-semibold ${
                                              turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.tier === 'tier-1-exact' ? 'text-green-700' :
                                              turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.tier === 'tier-2-tokenized' ? 'text-yellow-700' :
                                              turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.tier === 'tier-3-fuzzy' ? 'text-orange-700' :
                                              'text-gray-700'
                                            }`}>
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.tier === 'tier-1-exact' && '🎯 Tier 1 (Exact)'}
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.tier === 'tier-2-tokenized' && '🔍 Tier 2 (Tokenized)'}
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.tier === 'tier-3-fuzzy' && '✨ Tier 3 (Fuzzy)'}
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.tier && !turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.tier.startsWith('tier') && turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.tier}
                                            </span>
                                          </div>
                                          
                                          <div className="bg-white rounded p-2 border border-blue-100">
                                            <span className="text-gray-600">Duration:</span>
                                            <span className="ml-1 font-semibold text-gray-900">
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.durationMs}ms
                                            </span>
                                          </div>
                                          
                                          <div className="bg-white rounded p-2 border border-blue-100">
                                            <span className="text-gray-600">Results:</span>
                                            <span className="ml-1 font-semibold text-gray-900">
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.resultCount} found
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.filteredCount !== turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.resultCount && 
                                                ` → ${turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.filteredCount} after filters`
                                              }
                                            </span>
                                          </div>
                                        </div>
                                        
                                        {/* Applied Filters */}
                                        {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.appliedFilters && (
                                          <div className="mt-2">
                                            <span className="text-xs text-gray-600">Filters Applied:</span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.appliedFilters.priceRange && (
                                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Price Range</span>
                                              )}
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.appliedFilters.calorieRange && (
                                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Calorie Range</span>
                                              )}
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.appliedFilters.prioritizeSignature && (
                                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">⭐ Signature</span>
                                              )}
                                              {turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.appliedFilters.prioritizeHealthy && (
                                                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">🥗 Healthy</span>
                                              )}
                                              {!turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.appliedFilters.priceRange &&
                                               !turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.appliedFilters.calorieRange &&
                                               !turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.appliedFilters.prioritizeSignature &&
                                               !turn.plugin.pluginOutput.metadata.queries.itemQuery.searchMetadata.appliedFilters.prioritizeHealthy && (
                                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">None</span>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {renderJSON(
                                      turn.plugin.pluginOutput.metadata.queries.itemQuery,
                                      `plugin-item-query-${index}`,
                                    )}

                                    {/* Item Query Results */}
                                    {turn.plugin.pluginOutput.metadata.lookupResults?.items && (
                                      <div className="mt-3">
                                        <p className="text-xs font-semibold text-gray-600 mb-1">
                                          Item Lookup Results ({turn.plugin.pluginOutput.metadata.lookupResults.items.length} items):
                                        </p>
                                        {renderJSON(
                                          turn.plugin.pluginOutput.metadata.lookupResults.items,
                                          `plugin-item-results-${index}`,
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Store Query */}
                                {turn.plugin.pluginOutput.metadata.queries.storeQuery && (
                                  <div className="mb-3">
                                    <p className="text-xs font-semibold text-gray-600 mb-1">
                                      Store Lookup Query:
                                    </p>
                                    {renderJSON(
                                      turn.plugin.pluginOutput.metadata.queries.storeQuery,
                                      `plugin-store-query-${index}`,
                                    )}
                                  </div>
                                )}

                                {/* Website Query */}
                                {turn.plugin.pluginOutput.metadata.queries.websiteQuery && (
                                  <div className="mb-3">
                                    <p className="text-xs font-semibold text-gray-600 mb-1">
                                      Website Lookup Query:
                                    </p>
                                    {renderJSON(
                                      turn.plugin.pluginOutput.metadata.queries.websiteQuery,
                                      `plugin-website-query-${index}`,
                                    )}

                                    {/* Website Query Results */}
                                    {turn.plugin.pluginOutput.metadata.lookupResults
                                      ?.website_content && (
                                      <div className="mt-3">
                                        <p className="text-xs font-semibold text-gray-600 mb-1">
                                          Website Lookup Results:
                                        </p>
                                        {renderJSON(
                                          turn.plugin.pluginOutput.metadata.lookupResults
                                            .website_content,
                                          `plugin-website-results-${index}`,
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Show Plugin Output - only when turn.plugin exists */}
                            {turn.plugin && (
                              <>
                                <h6 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                                    Plugin Output
                                  </span>
                                </h6>
                                {renderJSON(
                                  (() => {
                                    const pluginOutput =
                                      turn.plugin.pluginOutput || turn.plugin.rawMessage;

                                // If pluginOutput has metadata.lookupResults, extract only the data
                                if (pluginOutput?.metadata?.lookupResults) {
                                  const lookupResults = pluginOutput.metadata.lookupResults;

                                  // Create a clean object with only the data fields (excluding _queryInfo)
                                  const cleanData: any = {};

                                  if (lookupResults.items) {
                                    cleanData.items = lookupResults.items;
                                  }
                                  if (lookupResults.stores) {
                                    cleanData.stores = lookupResults.stores;
                                  }
                                  if (lookupResults.website_content) {
                                    cleanData.website_content = lookupResults.website_content;
                                  }

                                  return cleanData;
                                }

                                // Otherwise return the original pluginOutput
                                return pluginOutput;
                              })(),
                              `plugin-${index}`,
                            )}
                              </>
                            )}
                            {/* For plugin steps without turn.plugin, show info from performance breakdown */}
                            {!turn.plugin && performanceData && getPluginStepInfo(performanceData) && (
                              <div className="text-xs text-gray-600">
                                <p className="mb-2">
                                  <strong>Step:</strong> {getPluginStepInfo(performanceData)?.name}
                                </p>
                                <p>
                                  <strong>Duration:</strong> {formatDuration(getPluginStepInfo(performanceData)?.duration || 0)}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* LLM Final Response */}
                        {(turn.llmFinal || (performanceData && hasLLMFinalStep(performanceData))) && (
                          <div className="bg-white rounded-lg p-4 border border-pink-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles size={16} className="text-pink-600" />
                              <h5 className="text-sm font-semibold text-gray-900">
                                LLM Final Response (Final Responder)
                              </h5>
                              {turn.llmFinal && (
                                <span className="text-xs text-gray-500">
                                  {formatTimeWithSeconds(turn.llmFinal.createdAt)}
                                </span>
                              )}
                              {performanceData && getProcessTime(performanceData, "llm_final") !== null && (
                                <span className={`ml-auto flex items-center gap-1 px-2 py-0.5 bg-pink-100 text-pink-700 rounded text-xs font-semibold ${!turn.llmFinal ? 'ml-0' : ''}`}>
                                  <Clock size={12} />
                                  {formatDuration(getProcessTime(performanceData, "llm_final"))}
                                </span>
                              )}
                            </div>

                            {/* Show Prompt if available - Always show if llmFinal exists */}
                            {turn.llmFinal && (
                              <div className="mb-4">
                                {turn.llmFinal.llmPrompt ? (
                                  <>
                                    <h6 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                        Prompt sent to LLM (Final Responder)
                                      </span>
                                    </h6>
                                    {renderPrompt(turn.llmFinal.llmPrompt, `llmfinal-prompt-${index}`)}
                                  </>
                                ) : (
                                  <div className="text-xs text-gray-400 italic">
                                    No prompt available for this LLM final response
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Show Response */}
                            <h6 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                              <span className="px-2 py-0.5 bg-pink-100 text-pink-700 rounded">
                                LLM Response
                              </span>
                            </h6>
                            {turn.llmFinal ? (
                              renderJSON(
                                turn.llmFinal.llmFinalResponse || turn.llmFinal.rawMessage,
                                `llmfinal-${index}`,
                              )
                            ) : turn.response?.rawMessage?.structuredResponse ? (
                              renderJSON(
                                turn.response.rawMessage.structuredResponse,
                                `llmfinal-${index}`,
                              )
                            ) : (
                              <span className="text-xs text-gray-400">No structured response available</span>
                            )}
                          </div>
                        )}

                        {/* Response to Frontend */}
                        {turn.response && (
                          <div className="bg-white rounded-lg p-4 border border-green-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Bot size={16} className="text-green-600" />
                              <h5 className="text-sm font-semibold text-gray-900">
                                Response to Frontend
                              </h5>
                              <span className="text-xs text-gray-500">
                                {formatTimeWithSeconds(turn.response.createdAt)}
                              </span>
                              {performanceData && getProcessTime(performanceData, "response") !== null && (
                                <span className="ml-auto flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-semibold">
                                  <Clock size={12} />
                                  {formatDuration(getProcessTime(performanceData, "response"))}
                                </span>
                              )}
                              {performanceData?.totalTime && (
                                <span className="ml-2 flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                                  Total: {formatDuration(performanceData.totalTime)}
                                </span>
                              )}
                            </div>
                            {renderJSON(turn.response.rawMessage, `response-${index}`)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionDetailPage;
